import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {HistoricalValuePresentationDTO} from '../../../api';
import {ColDef, ColGroupDef, ColumnApi, GridApi, GridOptions} from 'ag-grid-community';
import {HistoricalValuesService} from '../services/historical-values.service';
import {formatDate} from '@angular/common';

@Component({
  selector: 'app-historical-values-grid',
  templateUrl: './historical-values-grid.component.html',
  styleUrls: ['./historical-values-grid.component.scss']
})
export class HistoricalValuesGridComponent implements OnInit, OnChanges {

  @Input() dataGrid = [];

  private filterInformation;

  public gridOptions: GridOptions;
  public columnDefs: any[];
  public rowData: any[];
  private gridApi: GridApi;
  private columnApi: ColumnApi;
  public overlayNoRowsTemplate = 'Loading...';

  private historicalValues: HistoricalValuePresentationDTO[];

  public showLoadingIndicator = true;

  constructor(private historicalValuesService: HistoricalValuesService) {
  }

  ngOnInit() {
    this.registerFilteringEvents();

    this.gridOptions = <GridOptions>{};
    this.gridOptions.rowData = [];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.dataGrid) {
      this.historicalValues = changes.dataGrid.currentValue;
      this.filterTableBasedOnFilterInformation(this.filterInformation);
    }
  }

  private extractInformationForGridColumnDefinitions(historicalValues: Array<HistoricalValuePresentationDTO>) {
    const years: Set<string> = new Set();
    const dates: Set<string> = new Set();
    const headerFirstLevel: Set<string> = new Set();

    historicalValues.forEach((historicalValueDTO: HistoricalValuePresentationDTO) => {
      dates.add(historicalValueDTO.settlementDate);
      years.add(historicalValueDTO.productYear.toString());

      if (historicalValueDTO.productTypeName === 'gas') {
        headerFirstLevel.add(historicalValueDTO.marketHubName + ' ' + historicalValueDTO.periodDivisionLabel);
      }

      if (historicalValueDTO.productTypeName === 'electricity') {
        headerFirstLevel.add(historicalValueDTO.marketHubName +
          ' ' + historicalValueDTO.flavorName + ' ' + historicalValueDTO.periodDivisionLabel);
      }

    });

    return {
      years: Array.from(years).sort(),
      dates: Array.from(dates),
      headerFirstLevel: Array.from(headerFirstLevel)
    };
  }

  private initGridColumnsDefinitions(columnDefinitionsInfo) {
    this.columnDefs.push(this.buildDateColumn());
    this.buildHeaderFirstLevel(columnDefinitionsInfo).forEach((columnDefinition: ColGroupDef) => {
      this.columnDefs.push(columnDefinition);
    });
  }

  private buildDateColumn(): ColDef {
    const dateColumn: ColDef = {};
    dateColumn.headerName = 'Datum';
    dateColumn.width = 120;
    dateColumn.pinned = true;
    dateColumn.valueGetter = function (params) {
      return formatDate(params.data, 'DD.MM.YYYY', 'de_DE');
    };
    return dateColumn;
  }

  private buildHeaderFirstLevel(columnDefinitionsInfo): ColGroupDef[] {
    const level1Cols = [];

    columnDefinitionsInfo.headerFirstLevel.forEach((headerLabel: string) => {
      level1Cols.push({
        headerName: headerLabel,
        children: this.buildHeaderSecondLevel(columnDefinitionsInfo, headerLabel)
      });
    });

    return level1Cols;
  }

  private buildHeaderSecondLevel(columnDefinitionsInfo, marketHubPeriodDivisions): ColGroupDef[] {
    const level3Cols = [];
    const me = this;

    const numberOfYears = columnDefinitionsInfo.years.length;
    const calculatedColumnWidth = numberOfYears === 1 ? 220 : (numberOfYears === 2 ? 110 : 80);

    columnDefinitionsInfo.years.forEach((year: string) => {
      level3Cols.push({
        productName: marketHubPeriodDivisions + ' ' + year,
        headerName: year,
        width: calculatedColumnWidth,
        valueGetter(params) {
          const found = me.historicalValues.find((historicalValue: HistoricalValuePresentationDTO) => {
            return historicalValue.settlementDate === params.data
              && params.colDef.productName.indexOf(historicalValue.productName) >= 0
              && historicalValue.productYear === +year;
          });
          if (found && found.settlementValue) {
            return found.settlementValue.toFixed(2);
          }
        }
      });
    });

    return level3Cols;
  }

  private registerFilteringEvents() {
    this.historicalValuesService.filterSource$.subscribe(filterInformation => {
      this.showLoadingIndicator = true;
      this.filterInformation = filterInformation;
      this.filterTableBasedOnFilterInformation(filterInformation);
    });
  }

  private filterTableBasedOnFilterInformation(filterInformation) {
    if (filterInformation) {
      this.columnDefs = [];
      const columnDefinitionsBuildInformation = this.extractInformationForGridColumnDefinitions(this.historicalValues);

      this.applyYearsFilter(filterInformation.filtersData.years, columnDefinitionsBuildInformation);
      this.applyProductFilter(filterInformation.filtersData.periodDivisions, columnDefinitionsBuildInformation);
      this.applyProductFilter(filterInformation.filtersData.flavors, columnDefinitionsBuildInformation);
      this.applyProductFilter(filterInformation.filtersData.marketHubs, columnDefinitionsBuildInformation);

      this.initGridColumnsDefinitions(columnDefinitionsBuildInformation);
      this.rowData = columnDefinitionsBuildInformation.dates;
      this.showLoadingIndicator = false;
    }
  }

  private applyYearsFilter(years: any[], columnDefinitionsBuildInformation) {
    years.forEach(value => {
      if (value.checked === false) {
        for (let i = 0; i < columnDefinitionsBuildInformation.years.length; i++) {
          if (value.filter === columnDefinitionsBuildInformation.years[i]) {
            columnDefinitionsBuildInformation.years.splice(i, 1);
            i = i - 1;
          }
        }
      }
    });
  }

  private applyProductFilter(productInfo: any[], columnDefinitionsBuildInformation) {
    if (productInfo) {
      productInfo.forEach(value => {
        if (value.checked === false) {
          for (let i = 0; i < columnDefinitionsBuildInformation.headerFirstLevel.length; i++) {
            const headerLevelElements: string[] = columnDefinitionsBuildInformation.headerFirstLevel[i].split(' ');
            if (headerLevelElements.indexOf(value.filter) >= 0) {
              const idx = columnDefinitionsBuildInformation.headerFirstLevel.indexOf(columnDefinitionsBuildInformation.headerFirstLevel[i]);
              columnDefinitionsBuildInformation.headerFirstLevel.splice(idx, 1);
              i = i - 1;
            }
          }
        }
      });
    }
  }

  public onGridReady(params) {
    this.gridApi = params.api;
    this.columnApi = params.columnApi;
  }

}
